import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  ResponsiveContainer,
  CartesianGrid
} from "recharts";
import "./Charts.css";
import { Loader } from "../loader";

interface ChartProps {
  type: "bar" | "pie";
  data: any[];
  multiple?: boolean;
  heading: string;
  total?: number;
}

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" style={{
      fontSize: 12,
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: '12px ',
      letterSpacing: '0.15px',
      textTransform: 'uppercase'
    }}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const Charts: React.FC<ChartProps> = ({ type, data, multiple, heading, total }) => {
  const [activeTab, setActiveTab] = useState('retailer');
  let currentData: any = [];
  if (multiple) {
    currentData = data.find(item => item.userType === activeTab);
  }
 
  const renderLegend = (props: any) => {
    const { payload, total } = props;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <p style={{
          margin: 0, marginBottom: '10px', textAlign: 'left',

          color: 'rgba(0, 0, 0, 0.87)',

          fontSize: 14,
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: '157%',
          letterSpacing: '0.1px',


        }}>Total: {total}</p>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {
            payload.map((entry: any, index: number) => (
              <li key={`item-${index}`} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px',  color: '#607D8B',

                  fontSize: 12,
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '12px',
                  letterSpacing: '0.15px',
                  textTransform: 'uppercase' }}>
                <span style={{
                  backgroundColor: entry.color, width: '10px', height: '10px', marginRight: '10px', display: 'inline-block',



                 


                }}></span>
                {entry.value}
              </li>
            ))
          }
        </ul>
      </div>
    );
  };


  return (
    <div className="chart">
      <h3 className='heading'>
        {heading}
        {multiple && (
          <div className="tab-container">
            <button
              className={`tab-button ${activeTab === 'retailer' ? 'active' : ''}`}
              onClick={() => setActiveTab('retailer')}
            >
              Retailer
            </button>
            <button
              className={`tab-button ${activeTab === 'distributor' ? 'active' : ''}`}
              onClick={() => setActiveTab('distributor')}
            >
              Distributor
            </button>
          </div>
        )}
      </h3>
      <div className="chart-container">
        {(type === "bar" && data && data.length > 0) ? (
          <div className="bar-chart-section">
            <ResponsiveContainer width="100%" height={300}>
              {data && data.length > 0 ? (
                <BarChart width={500} height={300} data={data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="location" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="issued" stackId="a" fill="#4D8EB3" name="Certificates Issued" />
                  <Bar dataKey="not_issued" stackId="a" fill="#CFD8DC" name="Active Partners" />


                </BarChart>
              ) : <Loader />}
            </ResponsiveContainer>
            <div className="custom-legend-bar">
              <div className="legend-item">
                <span className="legend-dot" style={{ backgroundColor: '#4D8EB3' }}></span>
                <span>Certificates Issued</span>
              </div>
              <div className="legend-item">
                <span className="legend-dot" style={{ backgroundColor: '#CFD8DC' }}></span>
                <span>Active Partners</span>
              </div>
            </div>

          </div>
        ) :
          multiple ?
            (
              <div className="pie-chart-section pie-chart-section-row">
                <ResponsiveContainer width="50%" height={250}>
                  <PieChart>
                    <Pie
                      data={currentData.data}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={90}
                      label={renderCustomizedLabel}
                      labelLine={false}
                    >
                      {currentData.data.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>

                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="summary-text-container">
                  <p className="summary-total">Total: {currentData.total} Active {currentData.userType === 'retailer' ? 'Retailers' : 'Distributors'}</p>
                  <div className="summary-legend">
                    {currentData.data.map((item: any, index: number) => (
                      <div className="legend-item" key={index}>
                        <span className="legend-dot" style={{ backgroundColor: item.color }}></span>
                        <span className="legend-label">{item.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="pie-chart-section">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={data}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={100}
                      label={renderCustomizedLabel}
                      labelLine={false}
                    >
                      {data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Legend layout="vertical" align="right" content={(props) => renderLegend({ ...props, total })} />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                
              </div>
            )
        }
      </div>
    </div>

  );
};

export default Charts;
